// src/pages/profile-settings/patient/Dependants.jsx
import * as React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card.jsx";
import Snackbar from "@/sot/primitives/Snackbar";
import TopBar from "@/sot/primitives/TopBar";
import Sheet from "@/sot/primitives/Sheet";
import SaveChip from "@/sot/primitives/SaveChip";
import DatePicker from "@/sot/primitives/datePicker.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import ConsentModal from "@/components/ConsentModal";
import CoverCropModal from "@/components/media/CoverCropModal.jsx";
import { getSupabaseBaseUrl } from "@/services/supabaseClient";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import {
  hydrateDependantIdentityStatuses,
  presentDependantIdentityStatus,
} from "@/services/dependantIdentityVerificationService";

const ICON_PROFILE_AVATAR = "/icons/circular-avatar-placeholder.svg";
const ICON_PENDING_VERIFICATION = "/icons/doctor/pending-verification-badge.svg";
const ICON_VERIFIED = "/icons/doctor/verified_badge.svg";
const ICON_CHEVRON = "/icons/doctor/show_less.svg";
const ICON_CALENDAR = "/icons/list-items/appointment-calendar.svg";
const ICON_UPLOAD_IMAGE = "/icons/patient/upload_image.svg";

const DEV_KEY = "afrimed:dev:patient_dependants";
const PATIENT_DEPENDANT_AVATAR_BUCKET = "patient-profile-images";

const VERIFY_DOC_OPTIONS = [
  { value: "birth_certificate", label: "Birth Certificate", edgeDocType: "dependant_birth_certificate" },
  { value: "national_id", label: "National ID", edgeDocType: "dependant_national_id" },
  { value: "passport", label: "Passport", edgeDocType: "dependant_passport" },
];
const RELATIONSHIP_OPTIONS = [
  "Child",
  "Son",
  "Daughter",
  "Spouse",
  "Parent",
  "Sibling",
  "Other",
];

function normalizeDependantVerificationStatus(input) {
  const value = String(input || "").toLowerCase().trim();
  if (!value || value === "unverified") return "unverified";
  if (value === "admin_reviewed" || value === "document_reviewed") return "document_reviewed";
  if (value === "pending" || value === "pending_review") return "pending_review";
  if (value === "verified") return "verified";
  if (value === "rejected") return "rejected";
  return "unverified";
}

function SectionHeader({ title, actionLabel, onAction }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-body-medium text-on-surface-var">{title}</h2>
      {actionLabel && (
        <button
          type="button"
          onClick={onAction}
          className="text-body-medium text-primary"
        >
          {actionLabel}
        </button>
      )}
    </div>
  );
}

function isBypassAuth() {
  const bypassFlag = String(import.meta.env.VITE_BYPASS_AUTH || "").toLowerCase().trim();
  if (bypassFlag !== "true" && bypassFlag !== "1") return false;
  if (!import.meta.env.DEV) return false;

  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isLocal =
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host.endsWith(".local");

  return isLocal;
}

async function getStableUser(supabase) {
  const { data: sessionData, error: sessionErr } = await supabase.auth.getSession();
  if (sessionErr) throw sessionErr;

  let session = sessionData?.session || null;
  const now = Math.floor(Date.now() / 1000);
  const exp = session?.expires_at || 0;

  if (!session || exp - now < 60) {
    const { data: refreshed, error: refreshErr } = await supabase.auth.refreshSession();
    if (!refreshErr && refreshed?.session) {
      session = refreshed.session;
    }
  }

  if (session?.user) return session.user;

  const {
    data: { user },
    error: userErr,
  } = await supabase.auth.getUser();
  if (userErr) throw userErr;
  return user || null;
}

// ---------- DOB parsing (supports ISO + UK dd/mm/yyyy) ----------
function parseDobToLocalDate(dob) {
  if (!dob) return null;

  // If it's already a Date
  if (dob instanceof Date && !Number.isNaN(dob.getTime())) return dob;

  const raw = String(dob).trim();
  if (!raw) return null;

  // 1) ISO: YYYY-MM-DD (best case)
  // NOTE: new Date("YYYY-MM-DD") is treated as UTC in many browsers,
  // so we construct a local date manually to avoid day shifts.
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) {
    const y = Number(iso[1]);
    const m = Number(iso[2]); // 1-12
    const d = Number(iso[3]); // 1-31
    // Midday local avoids DST edge cases
    return new Date(y, m - 1, d, 12, 0, 0, 0);
  }

  // 2) UK: DD/MM/YYYY
  const uk = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (uk) {
    const d = Number(uk[1]);
    const m = Number(uk[2]); // 1-12
    const y = Number(uk[3]);
    return new Date(y, m - 1, d, 12, 0, 0, 0);
  }

  // 3) Fallback: let Date try (for "July 26, 1994" etc.)
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
}

function calcAgeFromDob(dob) {
  const d = parseDobToLocalDate(dob);
  if (!d) return null;

  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();

  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;

  // Guard against negative / insane ages from bad input
  if (age < 0 || age > 130) return null;

  return age;
}

function formatDateDisplay(value) {
  if (!value) return "";
  try {
    const d = parseDobToLocalDate(value) || new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);

    // ✅ UK markets
    return d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return String(value);
  }
}

function safeText(value) {
  return String(value || "").trim();
}

function extFromFile(file) {
  const name = String(file?.name || "");
  const dot = name.lastIndexOf(".");
  if (dot > -1) return name.slice(dot + 1).toLowerCase();

  const type = String(file?.type || "").toLowerCase();
  if (type.includes("png")) return "png";
  if (type.includes("webp")) return "webp";
  if (type.includes("jpeg") || type.includes("jpg")) return "jpg";
  return "jpg";
}

function resolveDependantAvatarSrc(row) {
  const directUrl = safeText(row?.avatarSrc || row?.avatarUrl || row?.avatar_url);
  if (directUrl) return directUrl;

  const path = safeText(row?.avatar_path || row?.avatarPath);
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;

  const base = getSupabaseBaseUrl();
  if (!base) return "";
  return `${base}/storage/v1/object/public/${PATIENT_DEPENDANT_AVATAR_BUCKET}/${path.replace(/^public\//, "")}`;
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Could not read image."));
    reader.readAsDataURL(file);
  });
}

async function hydrateDependantAvatarRows(supabase, rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  return Promise.all(
    list.map(async (row) => {
      const path = safeText(row?.avatar_path || row?.avatarPath).replace(/^public\//, "");
      if (!path) {
        return {
          ...row,
          avatarSrc: resolveDependantAvatarSrc(row),
        };
      }

      try {
        const { data, error } = await supabase.storage
          .from(PATIENT_DEPENDANT_AVATAR_BUCKET)
          .createSignedUrl(path, 60 * 60 * 24);

        if (!error && data?.signedUrl) {
          return {
            ...row,
            avatar_path: path,
            avatarSrc: data.signedUrl,
          };
        }
      } catch {
        // Public URL fallback below covers public buckets and old rows.
      }

      return {
        ...row,
        avatar_path: path,
        avatarSrc: resolveDependantAvatarSrc({ ...row, avatar_path: path }),
      };
    }),
  );
}

// The dependant row only retains a storage path today. Reconcile the display
// with the guardian-owned upload record without copying a review outcome into
// clinical, consent, access, or sponsorship authority.
async function hydrateDependantDocumentReviewRows(supabase, rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  const paths = [...new Set(list
    .map((row) => safeText(row?.verification_storage_path))
    .filter(Boolean))];
  if (!paths.length) return list;

  try {
    const { data, error } = await supabase
      .from("id_uploads")
      .select("path,status")
      .in("path", paths);
    if (error) return list;

    const statusByPath = new Map(
      (Array.isArray(data) ? data : []).map((upload) => [
        safeText(upload?.path),
        safeText(upload?.status).toLowerCase(),
      ]),
    );
    return list.map((row) => ({
      ...row,
      document_review_status: statusByPath.get(safeText(row?.verification_storage_path)) || "",
    }));
  } catch {
    return list;
  }
}

function DependantAvatar({ src = "", alt = "", size = "sm", button = false, onClick }) {
  const cleanSrc = safeText(src) || ICON_PROFILE_AVATAR;
  const isPlaceholder = cleanSrc === ICON_PROFILE_AVATAR;
  const sizeClass = size === "lg" ? "h-[84px] w-[84px]" : "h-8 w-8";
  const inner = (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-full bg-gradient-to-b from-white/80 to-white/0 mix-blend-overlay"
      />
      <img
        src={cleanSrc}
        alt={alt}
        className={[
          "relative h-full w-full rounded-full object-cover",
          isPlaceholder ? "bg-[rgb(var(--clr-surface))] p-1.5 opacity-90" : "",
        ].join(" ")}
        draggable="false"
      />
    </>
  );
  const className = [
    "relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[rgb(var(--clr-surface))]",
    "shadow-[0px_0px_0px_1px_rgba(24,50,119,0.10),0px_1.5px_0px_-0.5px_rgba(26,27,35,0.06),0px_3px_3px_-1px_rgba(26,27,35,0.06),0px_3px_6px_-2px_rgba(26,27,35,0.06),0px_6px_12px_-4px_rgba(29,79,215,0.06),0px_12px_24px_-10px_rgba(29,79,215,0.06)]",
    sizeClass,
  ].join(" ");

  if (button) {
    return (
      <button type="button" onClick={onClick} className={className}>
        {inner}
      </button>
    );
  }

  return <span className={className}>{inner}</span>;
}

function parseIsoDateLocal(iso) {
  const raw = String(iso || "").trim();
  const m = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return new Date();
  const y = Number(m[1]);
  const mm = Number(m[2]);
  const d = Number(m[3]);
  return new Date(y, (mm || 1) - 1, d || 1);
}

function toIsoDateLocal(dateLike) {
  const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function emptyDraft() {
  return {
    id: null,
    first_name: "",
    last_name: "",
    relationship: "Child",
    date_of_birth: "",
    avatar_path: "",
    avatar_url: "",
  };
}

function safeUUID() {
  // Modern browsers
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  // Fallback: RFC4122-ish v4 (good enough for local/dev IDs)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// If bypass storage ever had id:null, normalize it once.
function ensureIds(list) {
  const arr = Array.isArray(list) ? list : [];
  return arr.map((r) => {
    const id =
      r?.id && String(r.id).trim() !== "null" ? String(r.id) : safeUUID();
    return { ...r, id };
  });
}

function useTapGuard() {
  const lastTouchTs = React.useRef(0);

  const onTouchEnd = (fn) => (e) => {
    lastTouchTs.current = Date.now();
    // prevent Safari from turning this into a synthetic click later
    e.preventDefault();
    e.stopPropagation();
    fn?.(e);
  };

  const onClick = (fn) => (e) => {
    // ignore the synthetic click that follows a touch
    if (Date.now() - lastTouchTs.current < 600) return;
    fn?.(e);
  };

  return { onTouchEnd, onClick };
}

function getVerificationStatus(d) {
  const authoritative = String(d?.identity_verification_status || "").trim();
  return authoritative
    ? presentDependantIdentityStatus(authoritative)
    : normalizeDependantVerificationStatus(d?.document_review_status || d?.verification_status);
}

function VerificationAction({ status, onVerify }) {
  if (status === "verified") {
    return (
      <img
        src={ICON_VERIFIED}
        alt="Verified"
        className="h-5 w-auto shrink-0"
        draggable="false"
      />
    );
  }

  if (status === "document_reviewed") {
    return <span className="text-body-medium text-on-surface-var">Document reviewed</span>;
  }

  if (status === "pending_review") {
    return (
      <span className="text-body-medium text-on-surface-var">Pending identity verification</span>
    );
  }

  if (status === "rejected") {
    return (
      <button
        type="button"
        className="text-body-medium text-[#F8533A]"
        onClick={onVerify}
      >
        Re-verify
      </button>
    );
  }

  return (
    <button
      type="button"
      className="text-body-medium text-primary"
      onClick={onVerify}
    >
      Pending identity upload
    </button>
  );
}

export default function Dependants() {
  const nav = useNavigate();
  const location = useLocation();
  const { languageCode } = useLanguage();
  const isFrench = String(languageCode || "").toLowerCase().startsWith("fr");

  // Where to go back to (EditIntro or PatientProfile should pass this)
  const returnTo = location.state?.returnTo ?? -1;

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [mode, setMode] = React.useState("add"); // "add" | "edit"
  const [draft, setDraft] = React.useState(emptyDraft());
  const [showDobPicker, setShowDobPicker] = React.useState(false);
  const [relationshipMenuOpen, setRelationshipMenuOpen] = React.useState(false);
  const [avatarSrc, setAvatarSrc] = React.useState("");
  const [avatarFile, setAvatarFile] = React.useState(null);
  const [showAvatarCrop, setShowAvatarCrop] = React.useState(false);
  const avatarFileInputRef = React.useRef(null);
  const avatarCameraInputRef = React.useRef(null);
  const avatarBeforeCropRef = React.useRef("");
  const dobTriggerRef = React.useRef(null);
  const dobPickerRef = React.useRef(null);

  const [snSaved, setSnSaved] = React.useState(false);
  const [snRemoved, setSnRemoved] = React.useState(false);

  // ✅ show validation / save errors (so “dead” is never silent)
  const [snError, setSnError] = React.useState(false);
  const [snErrorMsg, setSnErrorMsg] = React.useState("");

  // --- Verify flow state ---
  const [verifyPickerOpen, setVerifyPickerOpen] = React.useState(false);
  const [verifyDocType, setVerifyDocType] = React.useState("birth_certificate");
  const [verifyCaptureMode, setVerifyCaptureMode] = React.useState("camera"); // camera | upload
  const [consentOpen, setConsentOpen] = React.useState(false);
  const [verifyTarget, setVerifyTarget] = React.useState(null); // dependant row
  const [verifyUploading, setVerifyUploading] = React.useState(false);
  const cameraFileRef = React.useRef(null);
  const uploadFileRef = React.useRef(null);

  const [snVerify, setSnVerify] = React.useState(false);
  const [snVerifyMsg, setSnVerifyMsg] = React.useState("");

  const tap = useTapGuard();

  // ✅ This is the ONLY “I’m done here” flag (TopBar SaveChip)
  const [dirty, setDirty] = React.useState(false);

  const goBack = React.useCallback(() => {
    nav(returnTo);
  }, [nav, returnTo]);

  const closeDobPicker = React.useCallback(() => {
    setShowDobPicker(false);
    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => dobTriggerRef.current?.focus());
    }
  }, []);

  React.useEffect(() => {
    if (!showDobPicker) return undefined;

    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeDobPicker();
    };
    const closeOnOutsidePointer = (event) => {
      const target = event.target;
      if (dobPickerRef.current?.contains(target) || dobTriggerRef.current?.contains(target)) return;
      closeDobPicker();
    };

    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOnOutsidePointer, true);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOnOutsidePointer, true);
    };
  }, [closeDobPicker, showDobPicker]);

  React.useEffect(() => {
    let alive = true;

    async function load() {
      try {
        // Dependants list
        if (isBypassAuth()) {
          try {
            const raw = localStorage.getItem(DEV_KEY);
            const parsed = raw ? JSON.parse(raw) : [];
            const data = ensureIds(parsed);
            if (alive) setRows(data);
            try {
              localStorage.setItem(DEV_KEY, JSON.stringify(data));
            } catch {
              // silent
            }
          } catch {
            if (alive) setRows([]);
          }
          return;
        }

        const { default: supabase } = await import("@/services/supabaseClient");
        const user = await getStableUser(supabase);
        if (!user) {
          if (alive) setRows([]);
          return;
        }

        const { data, error } = await supabase
          .from("patient_dependants")
          .select("*")
          .eq("user_id", user.id)
          .order("created_at", { ascending: true });

        if (error) throw error;
        const withDocumentReview = await hydrateDependantDocumentReviewRows(supabase, data);
        const withIdentityStatus = await hydrateDependantIdentityStatuses(supabase, withDocumentReview);
        const hydrated = await hydrateDependantAvatarRows(supabase, withIdentityStatus);
        if (alive) setRows(hydrated);
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, []);

  function openAdd() {
    setMode("add");
    setDraft(emptyDraft());
    setAvatarSrc("");
    setAvatarFile(null);
    setShowAvatarCrop(false);
    setShowDobPicker(false);
    setRelationshipMenuOpen(false);
    setSheetOpen(true);
  }

  function openEdit(row) {
    setMode("edit");
    setDraft({
      id: row.id,
      first_name: String(row.first_name ?? ""),
      last_name: String(row.last_name ?? ""),
      relationship: String(row.relationship ?? "Child"),
      date_of_birth: String(row.date_of_birth ?? ""),
      avatar_path: String(row.avatar_path ?? ""),
      avatar_url: String(row.avatar_url ?? ""),
    });
    setAvatarSrc(resolveDependantAvatarSrc(row));
    setAvatarFile(null);
    setShowAvatarCrop(false);
    setShowDobPicker(false);
    setRelationshipMenuOpen(false);
    setSheetOpen(true);
  }

  function closeSheet() {
    setSheetOpen(false);
    // ✅ NEVER set draft to null (that’s what caused the white screens)
    setDraft(emptyDraft());
    setAvatarSrc("");
    setAvatarFile(null);
    setShowAvatarCrop(false);
    setShowDobPicker(false);
    setRelationshipMenuOpen(false);
    setMode("add");
  }

  async function persistBypass(nextRows) {
    const clean = ensureIds(nextRows);
    try {
      localStorage.setItem(DEV_KEY, JSON.stringify(clean));
    } catch {
      // silent
    }
    setRows(clean);
  }

  function validateDraft(d) {
    const fn = String(d.first_name || "").trim();
    const ln = String(d.last_name || "").trim();
    const rel = String(d.relationship || "").trim();
    const dob = String(d.date_of_birth || "").trim();

    if (!fn) return "First name is required.";
    if (!ln) return "Last name is required.";
    if (!rel) return "Relationship is required.";
    if (!dob) return "Date of birth is required.";
    return "";
  }

  async function uploadDependantAvatar({
    supabase,
    userId,
    dependantId,
    file,
  }) {
    if (!supabase || !userId || !dependantId || !file) return null;
    const ext = extFromFile(file);
    const path = `dependants/${userId}/${dependantId}/avatar_${Date.now()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from(PATIENT_DEPENDANT_AVATAR_BUCKET)
      .upload(path, file, {
        cacheControl: "3600",
        upsert: true,
        contentType: file.type || undefined,
      });

    if (uploadError) throw uploadError;

    const { data: publicData } = supabase.storage
      .from(PATIENT_DEPENDANT_AVATAR_BUCKET)
      .getPublicUrl(path);

    let signedUrl = "";
    try {
      const { data: signedData } = await supabase.storage
        .from(PATIENT_DEPENDANT_AVATAR_BUCKET)
        .createSignedUrl(path, 60 * 60 * 24);
      signedUrl = signedData?.signedUrl || "";
    } catch {
      // Public URL fallback remains useful for public buckets.
    }

    return {
      avatar_path: path,
      avatar_url: publicData?.publicUrl || "",
      avatarSrc: signedUrl || publicData?.publicUrl || "",
    };
  }

  function triggerAvatarUpload() {
    const input = avatarFileInputRef.current;
    if (!input) return;
    try {
      if (typeof input.showPicker === "function") input.showPicker();
      else input.click();
    } catch {
      input.click();
    }
  }

  function triggerAvatarCamera() {
    const input = avatarCameraInputRef.current;
    if (!input) return;
    try {
      if (typeof input.showPicker === "function") input.showPicker();
      else input.click();
    } catch {
      input.click();
    }
  }

  async function onAvatarFilePicked(event) {
    const file = event?.target?.files?.[0];
    if (event?.target) event.target.value = "";
    if (!file) return;
    try {
      avatarBeforeCropRef.current = avatarSrc || resolveDependantAvatarSrc(draft) || "";
      setAvatarFile(file);
      const dataUrl = await fileToDataUrl(file);
      setAvatarSrc(dataUrl);
      setShowAvatarCrop(true);
    } catch {
      setSnErrorMsg("Could not load that photo. Please choose another image.");
      setSnError(true);
    }
  }

  async function handleSaveDraft() {
  // Prevent double-submit (common iOS “stuck” cause)
  if (saving) return;

  // iOS: if date input is focused, blur first or Safari can trap events/UI
  try {
    const el = document.activeElement;
    if (el && typeof el.blur === "function") el.blur();
  } catch {}

  // ✅ Normalize BEFORE validate (trim + defaults)
  const normalized = {
    ...draft,
    first_name: String(draft?.first_name || "").trim(),
    last_name: String(draft?.last_name || "").trim(),
    relationship: String(draft?.relationship || "").trim() || "Child",
    // keep as ISO yyyy-mm-dd string or null
    date_of_birth: String(draft?.date_of_birth || "").trim() || null,
    avatar_path: String(draft?.avatar_path || "").trim() || "",
    avatar_url: String(draft?.avatar_url || "").trim() || "",
  };

  const err = validateDraft(normalized);
  if (err) {
    setSnErrorMsg(err);
    setSnError(true);
    return;
  }

  // Lock UI for BOTH paths (prevents iOS “stuck” via rapid taps)
  setSaving(true);

  try {
    // BYPASS (dev/localStorage)
    if (isBypassAuth()) {
      const nowIso = new Date().toISOString();

      if (mode === "add") {
        const next = ensureIds([
          ...rows,
          {
            id: safeUUID(),
            created_at: nowIso,
            ...normalized,
            avatar_url: avatarFile ? avatarSrc || "" : normalized.avatar_url || avatarSrc || "",
            avatarSrc: avatarFile ? avatarSrc || "" : avatarSrc || normalized.avatar_url || "",
          },
        ]);
        await persistBypass(next);
      } else {
        const next = ensureIds(
          rows.map((r) =>
            String(r.id) === String(normalized.id)
              ? {
                  ...r,
                  ...normalized,
                  avatar_url: avatarFile ? avatarSrc || "" : normalized.avatar_url || r.avatar_url || "",
                  avatarSrc: avatarFile ? avatarSrc || "" : r.avatarSrc || resolveDependantAvatarSrc(r),
                }
              : r
          )
        );
        await persistBypass(next);
      }

      setSnSaved(true);
      setDirty(true);

      // Safari friendliness: let state flush before closing sheet
      await new Promise((r) => setTimeout(r, 0));
      closeSheet();
      return;
    }

    // NORMAL (Supabase)
    const { default: supabase } = await import("@/services/supabaseClient");
    const user = await getStableUser(supabase);
    if (!user) {
      console.warn("Dependants save: no user session");
      setSnErrorMsg("Please sign in again, then try Save.");
      setSnError(true);
      return;
    }

    // Since validation requires DOB, always send it (avoid weird partial payloads)
    const payload = {
      first_name: normalized.first_name,
      last_name: normalized.last_name,
      relationship: normalized.relationship,
      date_of_birth: normalized.date_of_birth,
    };

    if (mode === "add") {
      const { data, error } = await supabase
        .from("patient_dependants")
        .insert({ user_id: user.id, ...payload })
        .select("*")
        .single();

      if (error) throw error;

      let nextRow = data;
      if (avatarFile) {
        const avatarMeta = await uploadDependantAvatar({
          supabase,
          userId: user.id,
          dependantId: data.id,
          file: avatarFile,
        });
        if (avatarMeta?.avatar_path) {
          const { data: avatarRow, error: avatarErr } = await supabase
            .from("patient_dependants")
            .update({
              avatar_path: avatarMeta.avatar_path,
              avatar_url: avatarMeta.avatar_url || null,
            })
            .eq("id", data.id)
            .eq("user_id", user.id)
            .select("*")
            .single();
          if (avatarErr) throw avatarErr;
          if (avatarRow) {
            nextRow = {
              ...avatarRow,
              avatarSrc: avatarMeta.avatarSrc || avatarMeta.avatar_url || resolveDependantAvatarSrc(avatarRow),
            };
          }
        }
      }

      setRows((prev) => [...prev, nextRow]);
      setSnSaved(true);
      setDirty(true);

      await new Promise((r) => setTimeout(r, 0));
      closeSheet();
      return;
    }

    const { data, error } = await supabase
      .from("patient_dependants")
      .update(payload)
      .eq("id", normalized.id)
      .eq("user_id", user.id)
      .select("*")
      .single();

    if (error) throw error;

    const existingRow = rows.find((r) => String(r.id) === String(normalized.id));
    let nextRow = {
      ...data,
      avatarSrc: existingRow?.avatarSrc || resolveDependantAvatarSrc(data),
    };
    if (avatarFile) {
      const avatarMeta = await uploadDependantAvatar({
        supabase,
        userId: user.id,
        dependantId: normalized.id,
        file: avatarFile,
      });
      if (avatarMeta?.avatar_path) {
        const { data: avatarRow, error: avatarErr } = await supabase
          .from("patient_dependants")
          .update({
            avatar_path: avatarMeta.avatar_path,
            avatar_url: avatarMeta.avatar_url || null,
          })
          .eq("id", normalized.id)
          .eq("user_id", user.id)
          .select("*")
          .single();
        if (avatarErr) throw avatarErr;
        if (avatarRow) {
          nextRow = {
            ...avatarRow,
            avatarSrc: avatarMeta.avatarSrc || avatarMeta.avatar_url || resolveDependantAvatarSrc(avatarRow),
          };
        }
      }
    }

    setRows((prev) =>
      prev.map((r) => (String(r.id) === String(normalized.id) ? nextRow : r))
    );
    setSnSaved(true);
    setDirty(true);

    await new Promise((r) => setTimeout(r, 0));
    closeSheet();
  } catch (e) {
    console.error("Save dependant failed:", e);
    const raw = String(e?.message || "");
    if (raw.toLowerCase().includes("not accepting dependants")) {
      setSnErrorMsg("Please complete Health insurance first, then add your dependant.");
    } else {
      setSnErrorMsg(raw || "Save failed.");
    }
    setSnError(true);
  } finally {
    setSaving(false);
  }
  }

  function requestSaveDraft() {
    if (saving) return;
    try {
      const el = document.activeElement;
      if (el && typeof el.blur === "function") el.blur();
    } catch {}
    handleSaveDraft();
  }

  async function handleRemoveDraft() {
    const id = draft?.id;
    if (!id) return;

    // BYPASS
    if (isBypassAuth()) {
      const next = rows.filter((r) => String(r.id) !== String(id));
      await persistBypass(next);
      setSnRemoved(true);
      setDirty(true);
      closeSheet();
      return;
    }

    // NORMAL
    try {
      setSaving(true);

      const { default: supabase } = await import("@/services/supabaseClient");
      const user = await getStableUser(supabase);
      if (!user) return;

      const { error } = await supabase
        .from("patient_dependants")
        .delete()
        .match({ id, user_id: user.id });

      if (error) throw error;

      setRows((prev) => prev.filter((r) => String(r.id) !== String(id)));
      setSnRemoved(true);
      setDirty(true);
      closeSheet();
    } catch (e) {
      console.error("Remove dependant failed:", e);
    } finally {
      setSaving(false);
    }
  }

  function handleDone() {
  if (!dirty || saving) return;

  const stamp = Date.now();

  // Always prefer returning to an explicit route (so origin can refresh)
  if (typeof returnTo === "string" && returnTo.length > 0) {
    nav(returnTo, {
      replace: false, // ✅ IMPORTANT: ensure new location.key
      state: { dependantsUpdated: stamp },
    });
    return;
  }

  // fallback
  nav(-1);
}

  const canDone = dirty && !saving;

  const triggerCameraCapture = () => {
    const input = cameraFileRef.current;
    if (!input) return;
    try {
      if (typeof input.showPicker === "function") input.showPicker();
      else input.click();
    } catch {
      input.click();
    }
  };

  const triggerUploadBrowse = () => {
    const input = uploadFileRef.current;
    if (!input) return;
    try {
      if (typeof input.showPicker === "function") input.showPicker();
      else input.click();
    } catch {
      input.click();
    }
  };

  function defaultDocTypeForDependant(d) {
    const age = calcAgeFromDob(d?.date_of_birth || d?.dob);
    if (Number.isFinite(age) && age <= 14) return "birth_certificate";
    return "national_id";
  }

  function selectedDocMeta(value) {
    return (
      VERIFY_DOC_OPTIONS.find((opt) => opt.value === value) || VERIFY_DOC_OPTIONS[0]
    );
  }

  async function uploadVerificationDocument(file, selectedType) {
    const { uploadDocumentForOcr } = await import("@/lib/idVerificationFlow");
    return uploadDocumentForOcr({
      file,
      docType: selectedDocMeta(selectedType).edgeDocType,
      dependantId: verifyTarget?.id || "",
      retentionDays: 90,
    });
  }

  function openVerify(d) {
    setVerifyTarget(d);
    setVerifyDocType(defaultDocTypeForDependant(d));
    setVerifyPickerOpen(true);
  }

  function beginVerifyWithConsent(mode) {
    if (!verifyTarget?.id) return;
    setVerifyCaptureMode(mode);
    setVerifyPickerOpen(false);
    setConsentOpen(true);
  }

  async function persistVerifyBypass(dependantId, meta, status = "pending_review") {
    const next = rows.map((r) =>
      String(r.id) === String(dependantId)
        ? {
            ...r,
            verification_status: status,
            verification_storage_path: meta?.path || meta?.storage_path || null,
            verification_retention_expires_at: meta?.retention_expires_at || null,
          }
        : r
    );
    await persistBypass(next);
  }

  async function updateVerifyRowDb({
    supabase,
    userId,
    dependantId,
    payload,
  }) {
    const runUpdate = async (nextPayload) =>
      supabase
        .from("patient_dependants")
        .update(nextPayload)
        .eq("id", dependantId)
        .eq("user_id", userId)
        .select("*")
        .single();

    const first = await runUpdate(payload);
    if (!first.error) return first.data;

    const msg = String(first.error?.message || "").toLowerCase();
    const rejectedPendingReview =
      payload?.verification_status === "pending_review" &&
      (first.error?.code === "23514" ||
        msg.includes("check constraint") ||
        msg.includes("verification_status"));

    if (!rejectedPendingReview) throw first.error;

    const fallback = {
      ...payload,
      verification_status: "pending",
    };
    const second = await runUpdate(fallback);
    if (second.error) throw second.error;
    return second.data;
  }

  async function handleCaptureVerify(e) {
    const file = e.target.files?.[0];
    // allow re-selecting same image
    e.target.value = "";
    if (!file || !verifyTarget?.id) return;

    try {
      setVerifyUploading(true);

      // 1) Upload
      const selectedType = selectedDocMeta(verifyDocType);
      const meta = await uploadVerificationDocument(file, selectedType.value);
      const targetStatus = "pending_review";
      const storagePath = meta?.path || meta?.storage_path || null;

      if (!storagePath) {
        throw new Error("Upload failed: no storage path returned.");
      }

      // 2) Persist to local or Supabase
      if (isBypassAuth()) {
        await persistVerifyBypass(verifyTarget.id, meta, targetStatus);
      } else {
        const { default: supabase } = await import("@/services/supabaseClient");
        const user = await getStableUser(supabase);
        if (!user) {
          setSnVerifyMsg("Please sign in again, then try Verify.");
          setSnVerify(true);
          return;
        }

        const data = await updateVerifyRowDb({
          supabase,
          userId: user.id,
          dependantId: verifyTarget.id,
          payload: {
            verification_status: targetStatus,
            verification_storage_path: storagePath,
            verification_retention_expires_at: meta?.retention_expires_at || null,
          },
        });

        // update in-memory row
        const [withIdentityStatus] = await hydrateDependantIdentityStatuses(supabase, [data]);
        setRows((prev) => prev.map((r) => (String(r.id) === String(data.id) ? withIdentityStatus : r)));
      }

      // 3) UX feedback
      const isManual = selectedType.value === "birth_certificate";
      setSnVerifyMsg(
        isManual
          ? "Birth certificate uploaded. Sent for manual review."
          : "Document uploaded. OCR processing started."
      );
      setSnVerify(true);
    } catch (err) {
      console.error("Verify upload failed:", err);
      setSnVerifyMsg(err?.message || "Upload failed. Please try again.");
      setSnVerify(true);
    } finally {
      setVerifyUploading(false);
      setVerifyTarget(null);
      setVerifyPickerOpen(false);
    }
  }

  return (
    <div data-hide-tabbar="true" className="min-h-[100dvh]">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-24">
      <TopBar
        title="Dependants"
        subtitle="Add your loved ones to manage your family healthcare"
        onBack={goBack}
        trailing={<SaveChip disabled={!canDone} onClick={handleDone} />}
      />

      {/* Section header with Edit OUTSIDE card (PatientProfile style) */}
      <div className="mt-4">
        <SectionHeader
          title="" // no duplicate "Dependants" title here
          actionLabel="Edit"
          onAction={() => {
            if (rows.length > 0) openEdit(rows[0]);
            else openAdd();
          }}
        />

        <p className="mb-3 px-1 text-body-medium text-on-surface-var">
          A document review does not create a patient account, grant access to a health record,
          establish guardian authority, record care consent, or authorise payment.
        </p>

        <section className="mb-4 rounded-2xl border border-outline-var bg-surface px-4 py-4" aria-labelledby="dependant-identity-faq">
          <h2 id="dependant-identity-faq" className="text-title-small text-on-surface">
            {isFrench ? "Questions fréquentes sur l’identité" : "Dependant identity FAQ"}
          </h2>
          <dl className="mt-3 space-y-3 text-body-medium text-on-surface-var">
            <div>
              <dt className="font-medium text-on-surface">{isFrench ? "Quand le statut passe-t-il à « Vérification d’identité en attente » ?" : "When does the status become Pending identity verification?"}</dt>
              <dd>{isFrench ? "Après le téléversement d’un document valide associé à cette personne à charge. Avant cela, le statut est « Téléversement d’identité en attente »." : "After a valid document is submitted and bound to that specific dependant. Before that, it is Pending identity upload."}</dd>
            </div>
            <div>
              <dt className="font-medium text-on-surface">{isFrench ? "Une photo de profil suffit-elle ?" : "Is a profile photo enough?"}</dt>
              <dd>{isFrench ? "Non. Utilisez « Téléverser une pièce d’identité » et choisissez le document approprié." : "No. Use Upload ID and choose the appropriate identity or proof-of-age document."}</dd>
            </div>
            <div>
              <dt className="font-medium text-on-surface">{isFrench ? "La vérification donne-t-elle accès au dossier médical ?" : "Does verification grant access to a medical record?"}</dt>
              <dd>{isFrench ? "Non. L’autorité, le consentement et l’accès clinique sont décidés séparément pour chaque rendez-vous." : "No. Authority, consent and clinical access are decided separately for each appointment."}</dd>
            </div>
          </dl>
        </section>

        <Card className="overflow-hidden">
          {!loading && rows.length === 0 && (
            <div className="px-4 py-6 text-body-medium text-on-surface-var">
              No dependants added yet.
            </div>
          )}

          {rows.map((d, idx) => {
            // ✅ NEW: use the new DOB helpers
            const age = calcAgeFromDob(d?.date_of_birth || d?.dob);
            const isLast = idx === rows.length - 1;
            const safeKey = d?.id ? String(d.id) : `row-${idx}`;
            const iconSrc = resolveDependantAvatarSrc(d) || ICON_PROFILE_AVATAR;

            return (
              <div key={safeKey}>
                <button
                  type="button"
                  className="w-full text-left flex items-center px-4 py-3"
                  onClick={() => openEdit(d)}
                >
                  <span className="mr-3">
                    <DependantAvatar src={iconSrc} alt="" />
                  </span>

                  <div className="flex-1 min-w-0 flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="text-body-large text-on-surface truncate">
                        {d.first_name} {d.last_name}
                      </div>

                      <div className="text-body-medium text-on-surface-var">
                        {d.relationship}
                        {age !== null && ` (${age})`}
                      </div>
                    </div>

                    {/* RIGHT-ALIGNED VERIFY (single instance) */}
                    <div className="pl-4 pr-8 shrink-0">
                      <VerificationAction
                        status={getVerificationStatus(d)}
                        onVerify={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openVerify(d);
                        }}
                      />
                    </div>
                  </div>
                </button>

                {!isLast && <div className="mx-4 h-px bg-[rgba(var(--clr-outline-variant),0.55)]" />}
              </div>
            );
          })}
        </Card>
      </div>

      {/* Actions */}
      <div className="mt-6 flex gap-3">
        <Button variant="ghost" className="flex-1" onClick={goBack} disabled={saving}>
          Back
        </Button>
        <Button className="flex-1" onClick={openAdd} disabled={saving}>
          Add dependant
        </Button>
      </div>

      {/* Scrollable list dialog: keeps the dependent editor stable while its DOB picker opens. */}
      <Sheet
        open={sheetOpen}
        onClose={closeSheet}
        presentation="dialog"
        footer={
          <div className="flex gap-3">
            <button
              type="button"
              className="h-12 flex-1 rounded-[12px] bg-[rgba(var(--clr-on-surface),0.03)] text-primary text-body-medium disabled:opacity-60"
              disabled={saving}
              onClick={() => {
                if (!saving) closeSheet();
              }}
              style={{ touchAction: "manipulation" }}
            >
              Cancel
            </button>

            <button
              type="button"
              className="h-12 flex-1 rounded-[12px] bg-primary text-white text-body-medium disabled:opacity-60"
              disabled={saving}
              onTouchEnd={tap.onTouchEnd(() => requestSaveDraft())}
              onClick={tap.onClick(() => requestSaveDraft())}
              style={{ touchAction: "manipulation" }}
            >
              Save
            </button>
          </div>
        }
      >
        {/* The dialog primitive owns the 24px content rail. */}
        <div className="mx-auto w-full pb-6">
          <div>
            <div className="text-title-large text-on-surface">
              {mode === "add" ? "Add dependant" : "Edit dependant"}
            </div>
            <div className="mt-2 text-body-medium text-on-surface-var">
              {mode === "add"
                ? "Add a loved one under your account."
                : "Update details or remove this dependant."}
            </div>
          </div>

          <div className="mt-6 space-y-4">
            <div className="flex flex-col items-center gap-3 pb-2">
              <DependantAvatar
                button
                size="lg"
                src={avatarSrc}
                alt="Dependant avatar"
                onClick={triggerAvatarUpload}
              />

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={triggerAvatarUpload}
                  className="inline-flex items-center gap-2 rounded-[12px] bg-[rgba(var(--afrimed-primary),0.12)] px-4 py-2 text-body-medium text-primary"
                >
                  <img src={ICON_UPLOAD_IMAGE} alt="" className="h-5 w-5" />
                  Upload photo
                </button>
                <Button
                  variant="text"
                  size="default"
                  onClick={triggerAvatarCamera}
                  className="min-h-10 rounded-[12px] px-3 active:bg-[var(--state-primary-16)]"
                >
                  Camera
                </Button>
                {mode === "edit" ? (
                  <button
                    type="button"
                    onClick={() => {
                      const selected = rows.find((row) => String(row?.id) === String(draft?.id));
                      if (selected) openVerify(selected);
                    }}
                    className="inline-flex items-center rounded-[12px] border border-[rgba(var(--clr-outline-variant),0.55)] px-3 py-2 text-body-medium text-primary"
                  >
                    Upload ID
                  </button>
                ) : null}
              </div>
            </div>
            {/* First name */}
            <TextField56
              label="First name"
              placeholder="e.g. Dumisani"
              name="first_name"
              value={draft.first_name}
              onChange={(next) => setDraft((p) => ({ ...p, first_name: next }))}
              tone="surface"
            />

            {/* Last name */}
            <TextField56
              label="Last name"
              placeholder="e.g. Nyoni"
              name="last_name"
              value={draft.last_name}
              onChange={(next) => setDraft((p) => ({ ...p, last_name: next }))}
              tone="surface"
            />

            {/* Relationship */}
            <div className="w-full max-w-full">
              <div className="text-label-medium text-on-surface-var mb-1">
                Relationship
              </div>

              <div className="relative w-full max-w-full overflow-visible box-border">
                <button
                  type="button"
                  className={[
                    "w-full max-w-full box-border",
                    "h-12 px-4 pr-12 rounded-[12px]",
                    "border border-[rgba(var(--clr-outline-variant),0.95)] bg-[rgb(var(--clr-surface-cont-highest))] text-on-surface shadow-none",
                    "text-left",
                  ].join(" ")}
                  onClick={() => setRelationshipMenuOpen((open) => !open)}
                  aria-haspopup="listbox"
                  aria-expanded={relationshipMenuOpen}
                >
                  {String(draft.relationship || "").trim() || "Child"}
                </button>
                <img
                  src={ICON_CHEVRON}
                  alt=""
                  className={`absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 opacity-70 pointer-events-none transition-transform ${
                    relationshipMenuOpen ? "rotate-180" : ""
                  }`}
                />

                {relationshipMenuOpen ? (
                  <>
                    <button
                      type="button"
                      className="fixed inset-0 z-[58] cursor-default"
                      onClick={() => setRelationshipMenuOpen(false)}
                      aria-label="Close relationship options"
                    />
                    <div
                      className="
                        absolute left-0 right-0 top-[calc(100%+8px)]
                        z-[59]
                        rounded-[12px]
                        bg-[rgb(var(--clr-surface-cont-highest))]
                        border border-[rgba(15,23,42,0.08)]
                        shadow-[0px_12px_24px_-10px_rgba(29,79,215,0.10),0px_6px_12px_-4px_rgba(26,27,35,0.08)]
                        overflow-hidden
                      "
                      role="listbox"
                      aria-label="Relationship options"
                    >
                      {RELATIONSHIP_OPTIONS.map((option) => {
                        const selected = option === (String(draft.relationship || "").trim() || "Child");
                        return (
                          <button
                            key={option}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className="
                              w-full text-left px-4 py-3
                              text-body-large text-on-surface
                              hover:bg-[rgba(2,6,23,0.04)]
                              active:bg-[rgba(2,6,23,0.06)]
                              flex items-center justify-between
                            "
                            onClick={() => {
                              setDraft((p) => ({ ...p, relationship: option }));
                              setRelationshipMenuOpen(false);
                            }}
                          >
                            <span>{option}</span>
                            {selected ? (
                              <span className="text-primary text-body-medium">✓</span>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </>
                ) : null}
              </div>
            </div>

          {/* DOB */}
            <div className="w-full">
              <div className="text-label-medium text-on-surface-var mb-1">Date of birth</div>

              <button
                ref={dobTriggerRef}
                type="button"
                className={[
                  "relative w-full",
                  "h-12 rounded-[12px]",
                  "overflow-hidden border border-[rgba(var(--clr-outline-variant),0.95)] bg-[rgb(var(--clr-surface-cont-highest))] text-left shadow-none",
                ].join(" ")}
                onClick={() => {
                  if (showDobPicker) {
                    closeDobPicker();
                    return;
                  }
                  setShowDobPicker(true);
                }}
                aria-haspopup="dialog"
                aria-expanded={showDobPicker}
                style={{ touchAction: "manipulation" }}
              >
                <div className="h-full w-full px-4 pr-12 flex items-center text-on-surface">
                  {draft.date_of_birth ? formatDateDisplay(draft.date_of_birth) : "DD/MM/YYYY"}
                </div>

                <span
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-[20] h-8 w-8 inline-flex items-center justify-center pointer-events-none"
                  aria-hidden="true"
                >
                  <img
                    src={ICON_CALENDAR}
                    alt=""
                    className="h-5 w-5 opacity-80 pointer-events-none"
                    draggable="false"
                  />
                </span>
              </button>

              {showDobPicker ? (
                <div ref={dobPickerRef} className="mt-3 w-full max-w-[360px]">
                  <DatePicker
                    value={draft.date_of_birth ? parseIsoDateLocal(draft.date_of_birth) : new Date()}
                    commitOnSelect={false}
                    onConfirm={(dt) => {
                      const nextIso = toIsoDateLocal(dt);
                      setDraft((p) => ({ ...p, date_of_birth: nextIso }));
                      closeDobPicker();
                    }}
                    onCancel={closeDobPicker}
                    mode="single"
                  />
                </div>
              ) : null}
            </div>

          {mode === "edit" && (
            <button
              type="button"
              className="mt-3 h-12 w-full rounded-[12px] text-primary text-body-medium disabled:opacity-60"
              disabled={saving}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (saving) return;
                handleRemoveDraft();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (saving) return;
                handleRemoveDraft();
              }}
              style={{ touchAction: "manipulation" }}
            >
              Remove dependant
            </button>
          )}
          </div>
        </div>
      </Sheet>

      {/* Verify: document type + capture/upload choice */}
      <Sheet
        open={verifyPickerOpen}
        onClose={() => {
          if (verifyUploading) return;
          setVerifyPickerOpen(false);
          setVerifyTarget(null);
        }}
      >
        <div className="mx-auto w-full max-w-[720px] px-4 pb-6 pt-8">
          <div className="text-title-large text-on-surface">Verify dependant</div>
          <div className="mt-2 text-body-medium text-on-surface-var">
            Choose proof-of-age or identity document type.
          </div>

          <div className="mt-5 space-y-2">
            {VERIFY_DOC_OPTIONS.map((opt) => {
              const active = verifyDocType === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  className={[
                    "w-full rounded-[12px] px-4 py-3 text-left",
                    active
                      ? "bg-[rgba(29,79,215,0.10)] text-primary"
                      : "border border-[rgba(var(--clr-outline-variant),0.55)] bg-[rgb(var(--clr-surface))] text-on-surface shadow-[var(--elev-1)]",
                  ].join(" ")}
                  onClick={() => setVerifyDocType(opt.value)}
                  disabled={verifyUploading}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3">
            <Button
              variant="text"
              className="h-12 w-full rounded-[12px] active:bg-[var(--state-primary-16)] disabled:opacity-60"
              onClick={() => beginVerifyWithConsent("camera")}
              disabled={verifyUploading}
            >
              Use camera
            </Button>
            <button
              type="button"
              className="h-12 rounded-[12px] bg-primary text-white text-body-medium disabled:opacity-60"
              onClick={() => beginVerifyWithConsent("upload")}
              disabled={verifyUploading}
            >
              Upload file
            </button>
          </div>
        </div>
      </Sheet>

      {/* Verify: Consent → selected input mode */}
      <ConsentModal
        open={consentOpen}
        onCancel={() => {
          setConsentOpen(false);
          setVerifyTarget(null);
        }}
        onAccept={() => {
          if (verifyCaptureMode === "camera") triggerCameraCapture();
          else triggerUploadBrowse();
          setConsentOpen(false);
        }}
      />

      {/* Hidden file inputs */}
      <input
        ref={avatarFileInputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={onAvatarFilePicked}
      />
      <input
        ref={avatarCameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={onAvatarFilePicked}
      />

      <input
        ref={cameraFileRef}
        type="file"
        accept="image/*,application/pdf"
        capture="environment"
        className="sr-only"
        onChange={handleCaptureVerify}
        disabled={verifyUploading}
      />
      <input
        ref={uploadFileRef}
        type="file"
        accept="image/*,application/pdf"
        className="sr-only"
        onChange={handleCaptureVerify}
        disabled={verifyUploading}
      />

      <Snackbar open={snVerify} onClose={() => setSnVerify(false)} duration={2200}>
        {snVerifyMsg || "Submitted for verification"}
      </Snackbar>

      <Snackbar open={snSaved} onClose={() => setSnSaved(false)} duration={2200}>
        Saved
      </Snackbar>

      <Snackbar open={snRemoved} onClose={() => setSnRemoved(false)} duration={2200}>
        Removed
      </Snackbar>

      <Snackbar open={snError} onClose={() => setSnError(false)} duration={2500}>
        {snErrorMsg || "Couldn’t save dependant. Please try again."}
      </Snackbar>

      {showAvatarCrop && avatarSrc ? (
        <CoverCropModal
          src={avatarSrc}
          aspect={1}
          onCancel={() => {
            setAvatarFile(null);
            setAvatarSrc(avatarBeforeCropRef.current || resolveDependantAvatarSrc(draft) || "");
            setShowAvatarCrop(false);
          }}
          onConfirm={async (blob) => {
            try {
              const contentType = blob?.type || "image/jpeg";
              const ext = contentType.includes("png") ? "png" : "jpg";
              const nextFile = new File([blob], `dependant-avatar-${Date.now()}.${ext}`, {
                type: contentType,
              });
              setAvatarFile(nextFile);
              const preview = await fileToDataUrl(nextFile);
              setAvatarSrc(preview || URL.createObjectURL(blob));
              setShowAvatarCrop(false);
            } catch {
              setSnErrorMsg("Could not crop that photo. Please choose another image.");
              setSnError(true);
            }
          }}
        />
      ) : null}
      </div>
    </div>
  );
}
